import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, MessageSquare, Filter, FileText, ExternalLink, Lightbulb } from 'lucide-react';
import { useProjectFavorites } from '../../hooks/useProjectFavorites';
import { useMyFavorites } from '../../hooks/useMyHome';
import type { FavoriteItem } from '../../hooks/useMyHome';
import Spinner from '../ui/Spinner';

type TabId = 'project' | 'channel' | 'filter' | 'document' | 'idea';

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

const TABS: TabDef[] = [
  { id: 'project', label: '프로젝트', icon: FolderKanban },
  { id: 'channel', label: '채팅', icon: MessageSquare },
  { id: 'filter', label: '필터', icon: Filter },
  { id: 'document', label: '문서', icon: FileText },
  { id: 'idea', label: '아이디어', icon: Lightbulb },
];

function TabButton({
  tab,
  count,
  active,
  onClick,
}: {
  tab: TabDef;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
        active
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <tab.icon className="size-3.5" />
      {tab.label}
      {count > 0 && (
        <span className={`ml-0.5 px-1.5 py-0 text-[11px] rounded-full ${active ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── 프로젝트 탭 (기존 ProjectFavorite 재사용) ──
function ProjectTab() {
  const { data: favorites, isLoading } = useProjectFavorites();

  if (isLoading) return <Spinner />;

  if (!favorites || favorites.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-1">
      {favorites.map((p) => (
        <Link
          key={p.id}
          to={`/projects/${p.projectKey}`}
          state={{ projectId: p.id }}
          className="flex items-center justify-between px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors group"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center size-5 rounded bg-primary-100 text-primary-700 text-[11px] font-bold shrink-0">
              {p.projectKey.slice(0, 2)}
            </span>
            <span className="truncate text-gray-700">{p.name}</span>
            <span className="text-sm text-gray-400">({p.projectKey})</span>
          </div>
          <ExternalLink className="size-3.5 text-gray-300 group-hover:text-gray-500 shrink-0" />
        </Link>
      ))}
    </div>
  );
}

// ── 일반 즐겨찾기 탭 (CHANNEL, FILTER, DOCUMENT) ──
function GenericTab({ type }: { type: 'CHANNEL' | 'FILTER' | 'DOCUMENT' | 'IDEA' }) {
  const { data: favorites, isLoading } = useMyFavorites(type);

  if (isLoading) return <Spinner />;

  if (!favorites || favorites.length === 0) {
    return <EmptyState />;
  }

  const getLink = (item: FavoriteItem) => {
    switch (type) {
      case 'CHANNEL':
        return `/chat/${item.targetId}`;
      case 'FILTER':
        return `/filters/${item.targetId}`;
      case 'DOCUMENT':
        // 라우트는 /documents/:spaceId/:documentId — spaceId 없으면 빈 화면(로딩중...)에 갇힘
        return item.spaceId ? `/documents/${item.spaceId}/${item.targetId}` : '#';
      case 'IDEA':
        return `/ideas?node=${item.targetId}`;
      default:
        return '#';
    }
  };

  return (
    <div className="space-y-1">
      {favorites.map((item) => (
        <Link
          key={item.id}
          to={getLink(item)}
          className="flex items-center justify-between px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors group"
        >
          <span className="truncate text-gray-700">{item.name}</span>
          <ExternalLink className="size-3.5 text-gray-300 group-hover:text-gray-500 shrink-0" />
        </Link>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <p className="py-4 text-center text-sm text-gray-400">즐겨찾기한 항목이 없습니다</p>
  );
}

// ── 메인 컴포넌트 ──
export default function FavoriteTabs() {
  const [activeTab, setActiveTab] = useState<TabId>('project');

  // 카운트 조회 (프로젝트 탭은 기존 훅, 나머지는 useMyFavorites)
  const { data: projectFavorites } = useProjectFavorites();
  const { data: channelFavorites } = useMyFavorites('CHANNEL');
  const { data: filterFavorites } = useMyFavorites('FILTER');
  const { data: documentFavorites } = useMyFavorites('DOCUMENT');
  const { data: ideaFavorites } = useMyFavorites('IDEA');

  const counts: Record<TabId, number> = {
    project: projectFavorites?.length ?? 0,
    channel: channelFavorites?.length ?? 0,
    filter: filterFavorites?.length ?? 0,
    document: documentFavorites?.length ?? 0,
    idea: ideaFavorites?.length ?? 0,
  };

  return (
    <div>
      {/* 탭 바 */}
      <div className="flex bg-gray-100 rounded-lg p-0.5 mb-3 w-fit">
        {TABS.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            count={counts[tab.id]}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          />
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'project' && <ProjectTab />}
      {activeTab === 'channel' && <GenericTab type="CHANNEL" />}
      {activeTab === 'filter' && <GenericTab type="FILTER" />}
      {activeTab === 'document' && <GenericTab type="DOCUMENT" />}
      {activeTab === 'idea' && <GenericTab type="IDEA" />}
    </div>
  );
}
